import { useMemo } from 'react';
import type { InfiniteData } from '@tanstack/react-query';
import { generatePath } from 'react-router-dom';
import { SearchableModule } from '@sib/api';
import type {
  GlobalSearchBucketDto,
  GlobalSearchItemDto,
  GlobalSearchListDto,
} from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { paths } from '@sidecar/config/paths';
import {
  AuditTab,
  OrganizationTab,
} from '@sidecar/modules/organizations/types/organizationQueryParams.types';
import { ProjectTab } from '@sidecar/modules/projects/types/projects.types';
import { VendorTab } from '@sidecar/modules/vendors/types/types';

export const GLOBAL_SEARCH_ALL_TAB = 'all';

export const GLOBAL_SEARCH_MODULE_TAB_ORDER: SearchableModule[] = [
  SearchableModule.Tasks,
  SearchableModule.Audits,
  SearchableModule.Organizations,
  SearchableModule.Vendors,
  SearchableModule.Projects,
];

const GLOBAL_SEARCH_MODULE_LABEL_KEYS: Record<SearchableModule, string> = {
  [SearchableModule.Organizations]:
    'components.topbar.search.modules.organizations',
  [SearchableModule.Vendors]: 'components.topbar.search.modules.vendors',
  [SearchableModule.Audits]: 'components.topbar.search.modules.audits',
  [SearchableModule.Tasks]: 'components.topbar.search.modules.tasks',
  [SearchableModule.Projects]: 'components.topbar.search.modules.projects',
};

export const useGlobalSearchModuleLabels = () => {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      all: t('components.topbar.search.modules.all'),
      getModuleLabel: (module: SearchableModule) =>
        t(GLOBAL_SEARCH_MODULE_LABEL_KEYS[module]),
    }),
    [t]
  );
};

export const getNonEmptyGlobalSearchBuckets = (
  buckets?: GlobalSearchBucketDto[]
): GlobalSearchBucketDto[] => {
  const bucketsWithResults =
    buckets?.filter(bucket => bucket.totalItems > 0) ?? [];

  return [...bucketsWithResults].sort(
    (left, right) =>
      GLOBAL_SEARCH_MODULE_TAB_ORDER.indexOf(left.module) -
      GLOBAL_SEARCH_MODULE_TAB_ORDER.indexOf(right.module)
  );
};

export const getGlobalSearchTotalResults = (buckets: GlobalSearchBucketDto[]) =>
  buckets.reduce((sum, bucket) => sum + bucket.totalItems, 0);

export const resolveGlobalSearchActiveTab = (
  activeTab: string,
  buckets: GlobalSearchBucketDto[]
) =>
  activeTab === GLOBAL_SEARCH_ALL_TAB ||
  buckets.some(bucket => bucket.module === activeTab)
    ? activeTab
    : GLOBAL_SEARCH_ALL_TAB;

export const getGlobalSearchModuleItems = (
  data?: InfiniteData<GlobalSearchListDto>
): GlobalSearchItemDto[] => {
  const flattened = data?.pages.flatMap(page => page.items) ?? [];
  const seenIds = new Set<string>();

  return flattened.filter(item => {
    if (seenIds.has(item.id)) return false;

    seenIds.add(item.id);

    return true;
  });
};

export const getGlobalSearchItemPath = (
  item: GlobalSearchItemDto
): string | null => {
  switch (item.module) {
    case SearchableModule.Organizations:
      return generatePath(paths.organizations.details.tab, {
        organizationId: item.id,
        tab: OrganizationTab.Overview,
      });
    case SearchableModule.Vendors:
      return generatePath(paths.vendors.details.tab, {
        vendorId: item.id,
        tab: VendorTab.Information,
      });
    case SearchableModule.Projects:
      return generatePath(paths.projects.details.tab, {
        projectId: item.id,
        tab: ProjectTab.Overview,
      });
    case SearchableModule.Tasks:
      return null;
    case SearchableModule.Audits:
      if (!item.organizationId) return null;

      return generatePath(paths.organizations.details.auditDetailsTab, {
        organizationId: item.organizationId,
        auditId: item.id,
        auditTab: AuditTab.SpendSavings,
      });
  }
};
